import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantStore } from '@/stores/useTenantStore';
import { motion } from 'framer-motion';
import { CreditCard, Wrench, FileText, FileLock2, RefreshCw } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';

export const TenantDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { profile } = useAuth();
    const {
        dashboardData,
        loading,
        agreementAccepted,
        hasMultipleTenancies,
        checkAndInitializeState
    } = useTenantStore();

    useEffect(() => {
        const init = async () => {
            await checkAndInitializeState();
        };
        init();
    }, []);

    if (loading) {
        return <LoadingScreen />;
    }

    if (!agreementAccepted) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
                <FileLock2 size={80} className="text-primary mb-6" />
                <h2 className="text-2xl font-bold text-light-text dark:text-dark-text mb-4">Action Required</h2>
                <p className="text-light-subtle dark:text-dark-subtle mb-8 max-w-md">
                    Your lease agreement needs to be reviewed and accepted to access your dashboard.
                </p>
                <button
                    onClick={() => navigate('/tenant/documents')}
                    className="btn-primary"
                >
                    Review Agreement
                </button>
            </div>
        );
    }

    const { rent, notices, recentTransactions } = dashboardData || { rent: { amountDue: 0, dueDate: 'N/A', status: 'Paid' }, notices: [], recentTransactions: [] };

    const getStatusInfo = () => {
        if (rent.amountDue <= 0) {
            return { color: 'text-green-500', bg: 'bg-green-100', text: "All bills settled" };
        }
        if (rent.dueDate === "N/A") {
            return { color: 'text-red-500', bg: 'bg-red-100', text: "Amount is due" };
        }
        return {
            color: 'text-red-500', bg: 'bg-red-100',
            text: `Due on ${new Date(rent.dueDate).toLocaleDateString()}`
        };
    };

    const statusInfo = getStatusInfo();

    return (
        <div className="space-y-8 w-full">
            {/* Header section translated from RN padding */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">My Dashboard</h1>
                    <p className="text-light-subtle dark:text-dark-subtle mt-1">
                        Welcome back, {profile?.full_name || 'Tenant'}!
                    </p>
                </div>
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                    {hasMultipleTenancies && (
                        <button
                            onClick={() => navigate('/tenant/select-tenancy')}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        >
                            <RefreshCw size={18} />
                            Switch Tenancy
                        </button>
                    )}
                </div>
            </div>

            {/* Balance Card Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-primary rounded-xl p-8 text-white shadow-md text-center"
            >
                <p className="text-white/80 text-lg mb-2">Current Balance</p>
                <h2 className="text-5xl font-bold mb-4">
                    KES {rent.amountDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </h2>
                <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold ${statusInfo.bg} ${statusInfo.color}`}>
                    {statusInfo.text}
                </span>
            </motion.div>

            {/* Quick Actions translated from RN actions container */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <button
                    onClick={() => navigate('/tenant/payments')}
                    className="flex flex-col items-center justify-center p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-sm hover:shadow-md hover:bg-light-bg dark:hover:bg-dark-bg transition-all group"
                >
                    <CreditCard size={32} className="text-primary mb-3 " />
                    <span className="font-semibold text-primary">Make Payment</span>
                </button>
                <button
                    onClick={() => navigate('/tenant/maintenance')}
                    className="flex flex-col items-center justify-center p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-sm hover:shadow-md hover:bg-light-bg dark:hover:bg-dark-bg transition-all group"
                >
                    <Wrench size={32} className="text-primary mb-3 " />
                    <span className="font-semibold text-primary">Request Fix</span>
                </button>
                <button
                    onClick={() => navigate('/tenant/documents')}
                    className="flex flex-col items-center justify-center p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-sm hover:shadow-md hover:bg-light-bg dark:hover:bg-dark-bg transition-all group"
                >
                    <FileText size={32} className="text-primary mb-3 " />
                    <span className="font-semibold text-primary">Documents</span>
                </button>
            </div>

            {/* Notices and Transactions Cards section translated */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Notices */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 shadow-sm border border-light-border dark:border-dark-border">
                    <h3 className="text-lg font-bold text-primary dark:text-golden mb-4">Notices from Landlord</h3>
                    {notices && notices.length > 0 ? (
                        <div className="space-y-4">
                            {notices.map((notice: any, i: number) => (
                                <div key={i} className="p-4 bg-light-bg dark:bg-dark-bg rounded-lg border border-light-border dark:border-dark-border">
                                    <h4 className="font-semibold text-light-text dark:text-dark-text">{notice.title}</h4>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">{notice.message}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-light-subtle dark:text-dark-subtle italic text-center py-8">
                            No new notices.
                        </p>
                    )}
                </div>

                {/* Recent Transactions */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text">Recent Transactions</h3>
                        <button onClick={() => navigate('/tenant/payments')} className="text-sm text-primary hover:underline">View All</button>
                    </div>
                    {recentTransactions && recentTransactions.length > 0 ? (
                        <div className="space-y-3">
                            {recentTransactions.slice(0, 3).map((tx: any, i: number) => (
                                <div key={i} className="flex justify-between items-center p-3 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg border border-transparent hover:border-light-border dark:hover:border-dark-border transition-colors">
                                    <div>
                                        <p className="font-medium text-sm text-light-text dark:text-dark-text">{tx.description || 'Payment'}</p>
                                        <p className="text-xs text-light-subtle dark:text-dark-subtle">{new Date(tx.date).toLocaleDateString()}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-sm text-light-text dark:text-dark-text">KES {tx.amount.toLocaleString()}</p>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tx.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {tx.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-light-subtle dark:text-dark-subtle italic text-center py-8">
                            No recent transactions.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
